'use client';

import cn from 'classnames';
import BaseModal from '@/components/modal/lib/BaseModal';
import PriveeKeyholeMaroon from 'public/img/icon/keyhole-red.png';
import InfoIcon from 'public/img/icon/info.svg';
import Close from 'public/img/icon/close.svg';
import Image from 'next/image';
import Text from '@/components/common/Text/Text';

import './PriveeAcceptInvitationModal.style.scss';
import Button from '@/components/common/Button/Button';
import CheckBox from '@/components/common/CheckBox/CheckBox';
import Switch from '@/components/common/Switch/Switch';
import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useUser } from '@/components/hook/user';
import { AcceptPriveeError } from '@/components/context/UserContext/UserContext';
import TextWithAnimatedEllipsis from '@/components/common/TextWithAnimatedEllipsis/TextWithAnimatedEllipsis';
import { usePathname, useRouter } from 'next/navigation';
import { useCloset } from '@/components/hook/closet';
import { useFlags } from '@/components/hook/flags';
import { useIsMobileWeb } from '@/util/useIsMobileWeb';
import analytics, {
  trackPriveeWaitlistJoined,
  trackVipInsuranceSelectedAtAccept
} from '@/util/analytics';
import { isVipInsuranceFeatureEnabled } from '@/util/vip-insurance';
import { Tooltip, TooltipRefProps } from 'react-tooltip';

/** Display price for VIP Insurance on waitlist summary (matches product / ticket copy). */
const VIP_INSURANCE_PRICE_STR = '$85.00';
const VIP_INSURANCE_PRICE_NUMBER = 85;

type AcceptInvitationModalProps = {
  onClose: () => void;
  priveePriceStr: string;
  priveeInitFeeStr: string | null;
  variant?: 'default' | 'banner' | 'waitlist' | 'productCard';
};

function parsePriceNumber(priceStr: string): number {
  const parsed = Number(priceStr.replace(/[^0-9.]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatPriceNumber(amount: number): string {
  return `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
}

function AcceptInvitationModal(props: AcceptInvitationModalProps) {
  const {
    onClose,
    priveePriceStr,
    priveeInitFeeStr,
    variant = 'default'
  } = props;
  const isBannerVariant = variant === 'banner';
  const isWaitlistVariant = variant === 'waitlist';
  const isProductCardVariant = variant === 'productCard';
  const { value: flagValues } = useFlags();
  const isVipInsuranceEnabled = isVipInsuranceFeatureEnabled(flagValues);
  const showVipInsuranceCheckbox =
    isVipInsuranceEnabled &&
    !isWaitlistVariant &&
    (variant === 'default' || isBannerVariant);
  const showWaitlistVipControls = isVipInsuranceEnabled && isWaitlistVariant;
  const isMobileWeb = useIsMobileWeb();

  const {
    value: user,
    actions: { acceptPriveeInvitation, joinPriveeWaitlist }
  } = useUser();
  const {
    actions: { refreshCloset }
  } = useCloset();
  const [submissionStatus, setSubmissionStatus] = useState<
    'idle' | 'submitting' | 'error' | 'payment-error' | 'success'
  >('idle');
  const [postSuccessTemporaryMessage, setPostSuccessTemporaryMessage] =
    useState<'Refreshing' | 'Redirecting'>('Refreshing');
  const [isTermsAcknowledgementChecked, setIsTermsAcknowledgementChecked] =
    useState<boolean>(false);
  const [selectVipInsurance, setSelectVipInsurance] = useState<boolean>(false);
  const vipTooltipRef = useRef<TooltipRefProps>(null);

  const toggleIsTermsAcknowledgementChecked = () => {
    setIsTermsAcknowledgementChecked(!isTermsAcknowledgementChecked);
  };

  const toggleSelectVipInsurance = () => {
    setSelectVipInsurance(!selectVipInsurance);
  };

  const isFormLocked =
    submissionStatus === 'submitting' || submissionStatus === 'success';

  const waitlistMonthlyTotalStr = formatPriceNumber(
    parsePriceNumber(priveePriceStr) +
      (selectVipInsurance ? VIP_INSURANCE_PRICE_NUMBER : 0)
  );

  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    if (isWaitlistVariant) {
      analytics.track('Réservé Waitlist Modal - Viewed');
    } else {
      analytics.track('Réservé Accept Invitation Modal - Viewed', {
        variant: isBannerVariant ? 'banner' : 'default'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUnlockPriveeClick = async () => {
    if (isWaitlistVariant) {
      analytics.track('Réservé Join Waitlist - Clicked');
    } else {
      analytics.track('Réservé Accept Invitation - Clicked', {
        variant: isBannerVariant ? 'banner' : 'default'
      });
    }
    setSubmissionStatus('submitting');
    try {
      if (isWaitlistVariant) {
        await joinPriveeWaitlist(selectVipInsurance);
        trackPriveeWaitlistJoined(user, selectVipInsurance);
        setPostSuccessTemporaryMessage('Refreshing');
        setSubmissionStatus('success');
        // Page rerenders into the waitlist-joined variant from the dispatched
        // USER_UPDATE — no router.push needed.
        onClose();
      } else {
        trackVipInsuranceSelectedAtAccept(user, selectVipInsurance);
        await acceptPriveeInvitation(selectVipInsurance);
        analytics.track('Réservé Accept Invitation - Success', {
          variant: isBannerVariant ? 'banner' : 'default'
        });
        await refreshCloset();
        setSubmissionStatus('success');
        if (pathname.includes('/collection/reserve')) {
          // Hard refresh with `window.location.href` because:
          // `router.push('/collection/reserve')` (same path) does nothing,
          // `router.refresh()` doesn't close modal, and
          // simply closing the modal (even with a slight timeout) feels a little awkward
          setPostSuccessTemporaryMessage('Refreshing');
          window.location.href = '/collection/reserve';
        } else {
          setPostSuccessTemporaryMessage('Redirecting');
          router.push('/collection/reserve');
        }
      }
    } catch (error) {
      const isPaymentError =
        !isWaitlistVariant &&
        error instanceof AcceptPriveeError &&
        error.paymentFailed;
      if (isWaitlistVariant) {
        analytics.track('Réservé Join Waitlist - Failed');
      } else {
        analytics.track('Réservé Accept Invitation - Failed', {
          variant: isBannerVariant ? 'banner' : 'default',
          reason: isPaymentError ? 'payment_error' : 'generic_error'
        });
      }
      setSubmissionStatus(isPaymentError ? 'payment-error' : 'error');
    }
  };

  const submitButtonLabel: React.ReactNode = (() => {
    if (isWaitlistVariant) {
      if (submissionStatus === 'submitting') return 'Joining...';
      if (submissionStatus === 'success') return 'Joined!';
      return (
        <>
          Join waitlist
          <br /> to upgrade
        </>
      );
    }
    if (isBannerVariant) {
      if (submissionStatus === 'submitting') return 'Upgrading...';
      if (submissionStatus === 'success') return 'Upgraded!';
      return 'Upgrade to Réservé';
    }
    if (submissionStatus === 'submitting') return 'Unlocking Réservé...';
    if (submissionStatus === 'success') return 'Unlocked!';
    return 'Unlock Réservé';
  })();

  return (
    <BaseModal
      className='accept-invitation-modal'
      onClose={() => onClose()}
    >
      <div className='keyhole-img-container'>
        <Image
          src={PriveeKeyholeMaroon}
          alt='Maroon keyhole silhouette'
          width={26}
          height={53}
        />
      </div>
      <Text
        className='accept-invitation-title'
        variant='title-xl'
        italic
      >
        {isBannerVariant || isProductCardVariant ? (
          <>
            Accept your
            <br />
            R&eacute;serv&eacute; invitation!
          </>
        ) : isWaitlistVariant ? (
          <>
            Join the waitlist
            <br />
            to upgrade to R&eacute;serv&eacute;
          </>
        ) : (
          'Accept Your Invite'
        )}
      </Text>
      {(isBannerVariant || isProductCardVariant) && (
        <Text
          className='accept-invitation-body'
          variant='text-m'
        >
          R&eacute;serv&eacute; is an invite-only add-on tier within Vivrelle,
          offering access to an exclusive collection of ultra high-end
          accessories—from{' '}
          <span className='accept-invitation-body-bold'>
            Hermès Birkin and Kelly bags to Van Cleef &amp; Arpels, Bvlgari
            Serpenti, and more
          </span>
          . Members can borrow one item at a time with a 30-day swap cycle,
          supported by a dedicated VIP&nbsp;concierge.{' '}
          <span className='accept-invitation-body-bold'>
            R&eacute;serv&eacute; members also have the opportunity to purchase
            items from the R&eacute;serv&eacute; collection at preferred
            members-only pricing, with a portion of their membership fees
            applied towards their purchase.
          </span>
        </Text>
      )}
      {isWaitlistVariant && (
        <Text
          className='accept-invitation-body accept-invitation-body-bold'
          variant='text-m'
        >
          If accepted, your R&eacute;serv&eacute; membership will begin
          automatically. You&apos;ll be charged a prorated fee for the remainder
          of your current billing cycle, followed by the full monthly
          R&eacute;serv&eacute; fee on your next billing date.
        </Text>
      )}
      <div className='membership-summary-container'>
        <Text
          className='membership-summary-title'
          variant='title-xxs-700-upper'
        >
          R&eacute;serv&eacute; Member Summary
        </Text>
        <div className='membership-summary-items'>
          <div className='membership-summary-item'>
            <Text
              className='cost-label'
              variant='title-xxs-500-upper'
            >
              Monthly Membership:
            </Text>
            <Text className='cost-amount'>{priveePriceStr}</Text>
          </div>
          {showWaitlistVipControls && (
            <div className='membership-summary-item membership-summary-item-vip'>
              <div className='vip-summary-label-group'>
                <Switch
                  className={cn('waitlist-vip-switch', {
                    isDisabled: isFormLocked
                  })}
                  height={16}
                  width={32}
                  handleDiameter={14}
                  checked={selectVipInsurance}
                  onChange={(checked) => {
                    if (!isFormLocked) {
                      setSelectVipInsurance(checked);
                    }
                  }}
                />
                <Text
                  className='cost-label vip-summary-label'
                  variant='title-xxs-500-upper'
                >
                  VIP Insurance
                </Text>
                <button
                  type='button'
                  className='waitlist-vip-tooltip-anchor'
                  data-tooltip-id='waitlist-vip-insurance-tooltip'
                  aria-label='VIP Insurance information'
                >
                  <Image
                    src={InfoIcon}
                    alt=''
                    width={14}
                    height={14}
                  />
                </button>
              </div>
              <Text className='cost-amount'>{VIP_INSURANCE_PRICE_STR}</Text>
            </div>
          )}
          {showWaitlistVipControls && (
            <div className='membership-summary-item membership-summary-item-total'>
              <Text
                className='cost-label'
                variant='title-xxs-500-upper'
              >
                Monthly Total:
              </Text>
              <Text className='cost-amount'>{waitlistMonthlyTotalStr}</Text>
            </div>
          )}
          {!isWaitlistVariant && Boolean(priveeInitFeeStr) && (
            <div className='membership-summary-item'>
              <Text
                className='fee-label'
                variant='title-xxs-500-upper'
              >
                Initiation Fee:
              </Text>
              <Text className='fee-amount'>{priveeInitFeeStr}</Text>
            </div>
          )}
        </div>
        {isWaitlistVariant && (
          <div className='membership-summary-due-now'>
            <Text
              className='due-now-label'
              variant='title-xxs-700-upper'
            >
              Due Now:
            </Text>
            <Text
              className='due-now-amount'
              variant='title-xxs-700-upper'
            >
              $0
            </Text>
          </div>
        )}
      </div>
      {showWaitlistVipControls && (
        <Tooltip
          ref={vipTooltipRef}
          id='waitlist-vip-insurance-tooltip'
          className='waitlist-vip-insurance-tooltip'
          classNameArrow='waitlist-vip-insurance-tooltip-arrow'
          place='top'
          positionStrategy='fixed'
          openOnClick={isMobileWeb}
          clickable={isMobileWeb}
        >
          <div
            className={cn('waitlist-vip-insurance-tooltip-content', {
              'has-close': isMobileWeb
            })}
          >
            {isMobileWeb && (
              <button
                type='button'
                className='waitlist-vip-tooltip-close'
                aria-label='Close VIP Insurance information'
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  vipTooltipRef.current?.close();
                }}
              >
                <Image
                  src={Close}
                  alt=''
                  width={12}
                  height={12}
                />
              </button>
            )}
            <Text
              className='waitlist-vip-tooltip-title'
              variant='title-xxs-700-upper'
            >
              VIP Insurance
            </Text>
            <Text
              className='waitlist-vip-tooltip-body'
              variant='text-s'
            >
              R&eacute;serv&eacute; items are protected under Vivrelle&apos;s
              VIP coverage program. Standard and Premium coverage do not apply
              to R&eacute;serv&eacute; items.
            </Text>
          </div>
        </Tooltip>
      )}
      <div className='terms-acknowledgement-container'>
        <CheckBox
          className={cn('terms-acknowledgement-checkbox', {
            isDisabled: isFormLocked
          })}
          label=''
          onChange={() => {
            if (!isFormLocked) {
              toggleIsTermsAcknowledgementChecked();
            }
          }}
          checked={isTermsAcknowledgementChecked}
        />
        {isWaitlistVariant ? (
          <Text
            className='terms-acknowledgement-text'
            variant='text-s'
          >
            By accepting,{' '}
            <span className='terms-bold'>
              I acknowledge that, if approved from the waitlist, my
              R&eacute;serv&eacute; membership will be activated automatically.
              I understand that the monthly R&eacute;serv&eacute; add-on fee
              will renew automatically each month and is excluded from all
              promotional offers, discount codes, and credits unless explicitly
              stated. I understand I will initially be charged a prorated amount
              based on the time remaining until my next billing date, and that
              the full cost of R&eacute;serv&eacute;, along with the remainder
              of my Vivrelle membership fees, will be reflected on my following
              billing date.
            </span>{' '}
            I understand that R&eacute;serv&eacute; requires an active Vivrelle
            membership and may be canceled anytime in accordance with Vivrelle
            membership terms. I understand that Vivrelle&apos;s Standard and
            Premium coverage programs do not apply to R&eacute;serv&eacute;
            items.
          </Text>
        ) : (
          <Text
            className='terms-acknowledgement-text'
            variant='text-s'
          >
            By accepting,{' '}
            <span className='terms-bold'>
              I acknowledge that{' '}
              {!isBannerVariant &&
                Boolean(priveeInitFeeStr) &&
                'the Réservé initiation fee will be charged today and that '}
              the monthly R&eacute;serv&eacute; add-on fee will renew
              automatically each month and is excluded from all promotional
              offers, discount codes, and credits unless explicitly stated. I
              understand I will initially be charged a prorated amount based on
              the time remaining until my next billing date, and that the full
              cost of R&eacute;serv&eacute;, along with the remainder of my
              Vivrelle membership fees, will be reflected on my following
              billing date.
            </span>{' '}
            I understand that R&eacute;serv&eacute; requires an active Vivrelle
            membership and may be canceled anytime in accordance with Vivrelle
            membership terms.
          </Text>
        )}
      </div>
      {showVipInsuranceCheckbox && (
        <div
          className={cn('vip-insurance-container', {
            isDisabled: isFormLocked
          })}
        >
          <CheckBox
            className='vip-insurance-checkbox'
            label={
              <span className='vip-insurance-copy'>
                <Text
                  as='span'
                  variant='text-s'
                  className='vip-insurance-label'
                >
                  Add VIP Insurance ($85/mo)
                </Text>
                <Text
                  as='span'
                  variant='text-s'
                  className='vip-insurance-helper'
                >
                  Replaces your current coverage.
                </Text>
              </span>
            }
            onChange={() => {
              if (!isFormLocked) {
                toggleSelectVipInsurance();
              }
            }}
            checked={selectVipInsurance}
          />
        </div>
      )}
      {!isWaitlistVariant && submissionStatus === 'payment-error' && (
        <Text
          className='accept-invitation-payment-error-text'
          variant='text-s'
        >
          Your payment method was declined. Please update your payment details
          in{' '}
          <Link
            href='/account?section=payment'
            className='payment-error-my-account-link'
          >
            My Account
          </Link>{' '}
          to continue.
        </Text>
      )}
      <Button
        className={cn('accept-invitation-button', {
          'is-submitting': submissionStatus === 'submitting'
        })}
        variant='dark'
        onClick={() => handleUnlockPriveeClick()}
        disabled={
          !isTermsAcknowledgementChecked || submissionStatus === 'submitting'
        }
      >
        {submitButtonLabel}
      </Button>
      {submissionStatus === 'error' && (
        <Text
          className='accept-invitation-error-text'
          variant='text-s'
        >
          Sorry, something went wrong. Please refresh and try again. If the
          problem persists, please
          <br />
          contact us at{' '}
          <a
            href='mailto:vip@vivrelle.com'
            style={{ textDecoration: 'underline' }}
          >
            vip@vivrelle.com
          </a>
        </Text>
      )}
      {!isWaitlistVariant && submissionStatus === 'success' ? (
        <div className='post-success-text-with-ellipsis-container'>
          <TextWithAnimatedEllipsis
            text={postSuccessTemporaryMessage}
            textVariant='text-s'
            className='post-success-text-with-ellipsis'
          />
        </div>
      ) : submissionStatus !== 'submitting' ? (
        <Link
          className='terms-conditions-link'
          href='https://www.vivrelle.com/terms-reserve'
          target='_blank'
        >
          Terms & Conditions
        </Link>
      ) : null}
    </BaseModal>
  );
}

export default AcceptInvitationModal;
